<template>
  <b-modal
    :visible="visible"
    title="สแกนบัตรด้วยกล้อง"
    hide-footer
    centered
    @hide="$emit('close')"
  >
    <div class="voucher-camera-wrapper">
      <video
        ref="video"
        muted
        playsinline
        class="voucher-camera-video"
      ></video>
      <canvas ref="canvas" class="d-none"></canvas>
      <div v-if="status === 'scanning'" class="voucher-camera-frame"></div>
    </div>

    <p class="text-muted text-center">{{ helpText }}</p>

    <b-alert :show="!!lastRejected" variant="warning">{{ lastRejected }}</b-alert>

    <template v-if="status === 'denied'">
      <p class="text-center">
        กรุณาอนุญาตให้เว็บไซต์ใช้กล้องในการตั้งค่าเบราว์เซอร์ แล้วกดลองอีกครั้ง
      </p>
      <div class="text-center">
        <b-button variant="primary" @click="start">ลองอีกครั้ง</b-button>
      </div>
    </template>

    <div
      v-if="status === 'denied' || status === 'unsupported' || status === 'error'"
      class="text-center mt-2"
    >
      <b-button variant="outline-secondary" @click="$emit('close')">พิมพ์รหัสแทน</b-button>
    </div>
  </b-modal>
</template>

<script>
import jsQR from "jsqr";
import { normalizeCode, isValidChecksum } from "@/utils/voucherCodec";

const SCAN_INTERVAL_MS = 200; // 5 fps — plenty for a card held still, easy on the battery

export default {
  name: "VoucherCameraScanner",
  props: {
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      stream: null,
      timer: null,
      detector: null,
      status: "idle",     // idle | starting | scanning | denied | unsupported | error
      errorMessage: "",
      lastRejected: "",
    };
  },
  computed: {
    helpText() {
      return {
        starting: "กำลังเปิดกล้อง...",
        scanning: "หันกล้องไปที่ QR บนบัตรกำนัล",
        denied: "ไม่ได้รับอนุญาตให้ใช้กล้อง",
        // start() computes two DIFFERENT messages for this state — "this browser
        // cannot" vs "you must use https" — and they send an operator to very
        // different places. Read errorMessage rather than a literal, or that
        // distinction is computed and thrown away.
        unsupported: this.errorMessage || "อุปกรณ์นี้ใช้กล้องสแกนไม่ได้",
        error: this.errorMessage,
      }[this.status] || "";
    },
  },
  watch: {
    visible: {
      // immediate so the component also works if a parent mounts it with v-if
      // and visible already true — a plain watcher never fires on its initial
      // value, and the camera would simply never start.
      immediate: true,
      handler(open) {
        if (open) this.start();
        else this.stop();
      },
    },
  },
  methods: {
    async start() {
      this.status = "starting";
      this.errorMessage = "";
      this.lastRejected = "";

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        // Also the case on plain http:// — getUserMedia requires a secure context.
        this.status = "unsupported";
        this.errorMessage = window.isSecureContext
          ? "เบราว์เซอร์นี้ไม่รองรับการใช้กล้อง"
          : "ต้องเปิดผ่าน https จึงจะใช้กล้องได้";
        return;
      }

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
      } catch (error) {
        // NotAllowedError is the user saying no; everything else is hardware.
        if (error && (error.name === "NotAllowedError" || error.name === "SecurityError")) {
          this.status = "denied";
        } else {
          this.status = "error";
          this.errorMessage = "เปิดกล้องไม่สำเร็จ กรุณาลองใหม่หรือพิมพ์รหัสแทน";
        }
        return;
      }

      // Everything past this point must be guarded too. getUserMedia has already
      // succeeded, so this.stream is LIVE and the camera indicator is lit — an
      // unguarded throw here (video.play() rejecting on an autoplay policy, the
      // tab backgrounding mid-startup) leaves status stuck on "starting" forever,
      // and neither ลองอีกครั้ง nor พิมพ์รหัสแทน renders in that state.
      try {
        const video = this.$refs.video;
        video.srcObject = this.stream;
        video.setAttribute("playsinline", "true"); // iOS Safari refuses to inline-play without it
        await video.play();

        // Native first: BarcodeDetector is hardware-accelerated where it exists.
        if (typeof window.BarcodeDetector === "function") {
          try {
            this.detector = new window.BarcodeDetector({ formats: ["qr_code"] });
          } catch {
            this.detector = null; // constructed but qr_code unsupported — fall through to jsQR
          }
        }

        this.status = "scanning";
        this.timer = setInterval(this.tick, SCAN_INTERVAL_MS);
      } catch (error) {
        this.stop();  // releases the live stream rather than orphaning it
        this.status = "error";
        this.errorMessage = "เปิดกล้องไม่สำเร็จ กรุณาลองใหม่หรือพิมพ์รหัสแทน";
      }
    },

    async tick() {
      const video = this.$refs.video;
      if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) return;

      let raw = null;
      if (this.detector) {
        try {
          const found = await this.detector.detect(video);
          if (found && found.length) raw = found[0].rawValue;
        } catch {
          // A detector that throws mid-stream is not going to recover; drop to jsQR.
          this.detector = null;
        }
      }
      // stop() cannot cancel an await already in flight. If the operator closed
      // the modal while detect() was pending, the stream is already released and
      // the parent believes the scanner is gone — emitting now would deliver a
      // code nobody is listening for.
      if (this.status !== "scanning") return;
      if (raw === null) raw = this.decodeWithJsQr(video);
      if (!raw) return;

      const normalized = normalizeCode(raw);
      if (!isValidChecksum(normalized)) {
        // Keep scanning — the operator may have caught a different QR on the
        // counter. Show it once so a genuinely wrong card is not a silent no-op.
        this.lastRejected = "QR นี้ไม่ใช่รหัสบัตรกำนัลที่ถูกต้อง";
        return;
      }
      this.$emit("decoded", normalized);
    },

    decodeWithJsQr(video) {
      const canvas = this.$refs.canvas;
      const w = video.videoWidth, h = video.videoHeight;
      if (!w || !h) return null;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, w, h);
      const result = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, {
        inversionAttempts: "dontInvert", // printed cards are dark-on-light
      });
      return result ? result.data : null;
    },

    stop() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
      if (this.stream) {
        // Every track, or the tablet's camera light stays on.
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      const video = this.$refs.video;
      if (video) video.srcObject = null;
      this.detector = null;
      this.status = "idle";
    },
  },
  beforeDestroy() {
    this.stop();
  },
};
</script>

<style scoped>
.voucher-camera-wrapper {
  position: relative;
  width: 100%;
}

.voucher-camera-video {
  width: 100%;
  background-color: #000;
}

.voucher-camera-frame {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 60%;
  height: 60%;
  max-width: 240px;
  max-height: 240px;
  transform: translate(-50%, -50%);
  border: 3px solid #fff;
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.3);
  pointer-events: none;
}
</style>
